import {noop} from '@sail/utils';

type Fulfilled<T> = {
  status: 'fulfilled';
  value: T;
};

type Rejected = {
  status: 'rejected';
  reason: unknown;
};

type Pending = {
  status: 'pending';
};

export type FulfilledStatefulPromise<T> = Promise<T> & Fulfilled<T>;
export type RejectedStatefulPromise<T> = Promise<T> & Rejected;
export type PendingStatefulPromise<T> = Promise<T> & Pending;
export type PendingStatefulPromiseWithResolvers<T> =
  PendingStatefulPromise<T> & {
    resolve: (value: T) => void;
    reject: (reason: unknown) => void;
  };

export type StatefulPromise<T> =
  | FulfilledStatefulPromise<T>
  | RejectedStatefulPromise<T>
  | PendingStatefulPromise<T>;

export type StatefulPromiseWithResolvers<T> =
  | FulfilledStatefulPromise<T>
  | RejectedStatefulPromise<T>
  | PendingStatefulPromiseWithResolvers<T>;

/**
 * Transforms a promise into a stateful promise by adding a status property and value/reason.
 * This allows synchronously checking a promise's state, which isn't possible with regular promises.
 *
 * Augmented promises are valid thenables (https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Promise#thenables)
 * React implementation (https://github.com/facebook/react/blob/97cdd5d3c33eda77be4f96a43f72d6916d3badbb/packages/shared/ReactTypes.js#L112-L138)
 * and react-query (https://github.com/TanStack/query/blob/35d5d55aa72a9e39284554852983b0745deecc52/packages/query-core/src/thenable.ts)
 */
export function transformToStatefulPromise<T>(
  promise: Promise<T> | StatefulPromise<T>,
): StatefulPromise<T> {
  if ('status' in promise) {
    return promise;
  }
  const statefulPromise = Object.assign(promise, {
    status: 'pending' as const,
  });

  statefulPromise.then(
    (value) => {
      Object.assign(statefulPromise, {
        status: 'fulfilled' as const,
        value,
      });
    },
    (reason) => {
      Object.assign(statefulPromise, {
        status: 'rejected' as const,
        reason,
      });
    },
  );

  return statefulPromise;
}

export function createStatefulPromiseResolvers<
  T,
>(): StatefulPromiseWithResolvers<T> {
  let resolver: (value: T) => void = noop;
  let rejector: (reason: unknown) => void = noop;

  const promise = new Promise<T>((resolve, reject) => {
    resolver = resolve;
    rejector = reject;
  });

  const statefulPromise = transformToStatefulPromise(promise);

  if (statefulPromise.status === 'pending') {
    return Object.assign(statefulPromise, {
      resolve: resolver,
      reject: rejector,
    });
  }
  return statefulPromise;
}
